import { useEffect, useState, useCallback } from "react";

const PALETTE_EVENT = "ailechat:palette-toggle";
const HELP_EVENT = "ailechat:help-toggle";
const SIDEBAR_EVENT = "ailechat:sidebar-toggle";
const CONTEXT_EVENT = "ailechat:context-toggle";

export type ContextTab = "members" | "files" | "pinned" | "activity";

/* ----- Command palette ----- */

export function openCommandPalette(): void {
  window.dispatchEvent(new CustomEvent(PALETTE_EVENT, { detail: true }));
}

export function closeCommandPalette(): void {
  window.dispatchEvent(new CustomEvent(PALETTE_EVENT, { detail: false }));
}

export function useCommandPaletteOpen(): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onToggle = (e: Event) => {
      const detail = (e as CustomEvent<boolean>).detail;
      setOpen((prev) => (typeof detail === "boolean" ? detail : !prev));
    };
    window.addEventListener(PALETTE_EVENT, onToggle);
    return () => window.removeEventListener(PALETTE_EVENT, onToggle);
  }, []);
  return [open, setOpen];
}

/* ----- Help dialog ----- */

export function toggleHelpDialog(open?: boolean): void {
  window.dispatchEvent(new CustomEvent(HELP_EVENT, { detail: open }));
}

export function useHelpDialog(): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onToggle = (e: Event) => {
      const detail = (e as CustomEvent<boolean | undefined>).detail;
      setOpen((prev) => (typeof detail === "boolean" ? detail : !prev));
    };
    window.addEventListener(HELP_EVENT, onToggle);
    return () => window.removeEventListener(HELP_EVENT, onToggle);
  }, []);
  return [open, setOpen];
}

/* ----- Sidebar / Context toggle (persists) ----- */

const SIDEBAR_KEY = "ailechat:sidebarOpen";
const CONTEXT_KEY = "ailechat:contextOpen";

function readBool(key: string, def: boolean): boolean {
  if (typeof localStorage === "undefined") return def;
  const v = localStorage.getItem(key);
  if (v === null) return def;
  return v === "1";
}

function writeBool(key: string, val: boolean): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(key, val ? "1" : "0");
}

export function useSidebarOpen(): [boolean, (v?: boolean) => void] {
  const [open, setOpen] = useState<boolean>(() => window.innerWidth < 768 ? false : readBool(SIDEBAR_KEY, true));
  useEffect(() => {
    const onToggle = (e: Event) => {
      const detail = (e as CustomEvent<boolean | undefined>).detail;
      setOpen((prev) => {
        const next = typeof detail === "boolean" ? detail : !prev;
        if (window.innerWidth >= 768) writeBool(SIDEBAR_KEY, next);
        return next;
      });
    };
    window.addEventListener(SIDEBAR_EVENT, onToggle);
    return () => window.removeEventListener(SIDEBAR_EVENT, onToggle);
  }, []);
  const set = useCallback((v?: boolean) => {
    window.dispatchEvent(new CustomEvent(SIDEBAR_EVENT, { detail: v }));
  }, []);
  return [open, set];
}

export function useContextOpen(onTabChange?: (tab: ContextTab) => void): [boolean, (v?: boolean) => void] {
  const [open, setOpen] = useState<boolean>(() => window.innerWidth < 768 ? false : readBool(CONTEXT_KEY, true));
  useEffect(() => {
    const onToggle = (e: Event) => {
      const detail = (e as CustomEvent<boolean | ContextTab | undefined>).detail;
      if (typeof detail === "string") onTabChange?.(detail);
      setOpen((prev) => {
        const next = typeof detail === "string" ? true : typeof detail === "boolean" ? detail : !prev;
        if (window.innerWidth >= 768) writeBool(CONTEXT_KEY, next);
        return next;
      });
    };
    window.addEventListener(CONTEXT_EVENT, onToggle);
    return () => window.removeEventListener(CONTEXT_EVENT, onToggle);
  }, [onTabChange]);
  const set = useCallback((v?: boolean) => {
    window.dispatchEvent(new CustomEvent(CONTEXT_EVENT, { detail: v }));
  }, []);
  return [open, set];
}

export function toggleSidebar(open?: boolean): void {
  window.dispatchEvent(new CustomEvent(SIDEBAR_EVENT, { detail: open }));
}

export function toggleContext(open?: boolean): void {
  window.dispatchEvent(new CustomEvent(CONTEXT_EVENT, { detail: open }));
}

export function openContextTab(tab: ContextTab): void {
  window.dispatchEvent(new CustomEvent(CONTEXT_EVENT, { detail: tab }));
}
